import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { quizAPI, submissionAPI } from '../services/api';
import TakeQuiz from '../components/TakeQuiz';

function TakeQuizById() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [quiz, setQuiz] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [isRetake, setIsRetake] = useState(false);
  const [attemptNumber, setAttemptNumber] = useState(1);
  const [retakeShuffledOptions, setRetakeShuffledOptions] = useState({});

  useEffect(() => {
    if (quizId) {
      // Check if this is a retake (quiz data passed through navigation state)
      if (location.state?.isRetake && location.state?.quiz) {
        setQuiz(location.state.quiz);
        setIsRetake(true);
        setAttemptNumber(location.state.attemptNumber || 1);
        setRetakeShuffledOptions(location.state.shuffledOptions || {});
        setLoading(false);
      } else {
        fetchQuiz();
      }
    }
  }, [quizId, location.search]);

  const fetchQuiz = async () => {
    setError('');
    setLoading(true);

    try {
      // Check if this is a retake from URL params
      const searchParams = new URLSearchParams(location.search);
      const isRetakeParam = searchParams.get('retake') === 'true';
      
      if (isRetakeParam) {
        // For retakes, call the initiateRetake endpoint to get all necessary data
        const response = await submissionAPI.initiateRetake(quizId);
        setQuiz(response.data.quiz);
        setIsRetake(true);
        setAttemptNumber(response.data.attemptNumber);
        setRetakeShuffledOptions(response.data.shuffledOptions || {});
      } else {
        // Regular quiz access
        const response = await quizAPI.getQuizById(quizId);
        setQuiz(response.data);
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      // Handle already taken quiz
      if (errorData?.alreadyTaken) {
        setError('✅ You have already taken this quiz. Multiple attempts are not allowed.');
      }
      // Handle scheduled quiz messages
      else if (errorData?.notStarted) {
        const startDate = new Date(errorData.startDate);
        setError(`⏰ This quiz hasn't started yet. It will be available from ${startDate.toLocaleString()}`);
      } else if (errorData?.ended) {
        const endDate = new Date(errorData.endDate);
        setError(`⏱️ This quiz has ended. It was available until ${endDate.toLocaleString()}`);
      } else {
        setError(errorData?.message || 'Quiz not found');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleQuizComplete = () => {
    // Navigate back or to results page
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', paddingTop: '80px' }}>
        <div className="loading">Loading quiz...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card" style={{ maxWidth: '540px', margin: '0 auto', textAlign: 'center' }}>
          <div className="error" style={{ marginBottom: '20px' }}>{error}</div>
          <button 
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  if (quiz) {
    return (
      <TakeQuiz 
        quiz={quiz} 
        onComplete={handleQuizComplete}
        isRetake={isRetake}
        attemptNumber={attemptNumber}
        retakeShuffledOptions={retakeShuffledOptions}
      />
    );
  }

  return null;
}

export default TakeQuizById;
